import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from './useAuth';
import {
  analyticsPage,
  analyticsTrack,
  type AnalyticsEvent,
} from '../services/analytics';
import {
  balanceSelector,
  type BalanceSliceState,
  BALANCE_SLICE_KEY,
} from '../store/slices/balanceSlice';
import {
  gameSelector,
  type GameSliceState,
  GAME_SLICE_KEY,
} from '../store/slices/gameSlice';
import {
  roundSelector,
  type RoundSliceState,
  ROUND_SLICE_KEY,
} from '../store/slices/roundSlice';

type AnalyticsState = {
  [BALANCE_SLICE_KEY]: BalanceSliceState;
  [GAME_SLICE_KEY]: GameSliceState;
  [ROUND_SLICE_KEY]: RoundSliceState;
};

export interface AnalyticsCommonProps {
  userId: number | null;
  sessionId: string | null;
  gameId: string | null;
  roundId: string | null;
  balance: number;
  currency: string;
}

export function useAnalytics() {
  const { user } = useAuth();
  const { gameConfig } = useSelector<AnalyticsState, GameSliceState>(
    gameSelector
  );
  const { roundId } = useSelector<AnalyticsState, RoundSliceState>(
    roundSelector
  );
  const { balance } = useSelector<AnalyticsState, BalanceSliceState>(
    balanceSelector
  );

  const commonProps = useMemo<AnalyticsCommonProps>(
    () => ({
      userId: user?.id ?? null,
      sessionId: user?.extraData.session ?? null,
      gameId: gameConfig?.gameId ?? null,
      roundId: roundId ?? null,
      balance: balance.amount,
      currency: balance.userCurrency,
    }),
    [
      user?.id,
      user?.extraData.session,
      gameConfig?.gameId,
      roundId,
      balance.amount,
      balance.userCurrency,
    ]
  );

  const commonPropsRef = useRef(commonProps);
  commonPropsRef.current = commonProps;

  const trackEvent = useCallback(
    (event: AnalyticsEvent, extraProps?: Record<string, unknown>) => {
      analyticsTrack(event, { ...commonPropsRef.current, ...extraProps });
    },
    []
  );

  const trackPage = useCallback(
    (event: AnalyticsEvent, extraProps?: Record<string, unknown>) => {
      analyticsPage(event, { ...commonPropsRef.current, ...extraProps });
    },
    []
  );

  return { trackEvent, trackPage, commonProps };
}
